import { t } from "@travily/i18n";
import { Icon, PaperPlaneIcon } from "@ui/components/Icon";
import { usePlatform } from "@ui/hooks/use-platform";
import { Button } from "@ui/primitives/Button";
import { AutoGrowTextArea, TextField } from "@ui/primitives/TextField";
import { type KeyboardEvent, useRef } from "react";
import { ComposerAttachments } from "@/components/ComposerAttachments/ComposerAttachments";
import { ALLOWED_IMAGE_TYPES, MAX_ATTACHMENTS, type UploadFile } from "@/hooks/use-direct-upload-multiple";

interface MessageComposerProps {
  attachments: UploadFile[];
  isSending: boolean;
  messageValue: string;
  onAddFiles: (files: FileList) => void;
  onMessageChange: (value: string) => void;
  onRemoveFile: (id: string) => void;
  onSendMessage: () => void;
  /** Fired on every change — the throttle that turns this into a ping lives in the hook. */
  onTyping?: () => void;
}

export function MessageComposer({
  attachments,
  isSending,
  messageValue,
  onAddFiles,
  onMessageChange,
  onRemoveFile,
  onSendMessage,
  onTyping,
}: MessageComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploading = attachments.some((file) => file.status === "uploading");
  // A picture on its own is a message; words are not required when there is one. Failed uploads
  // never become signed ids, so they are not content.
  const hasSendableAttachment = attachments.some((file) => file.status !== "failed");
  const isEmpty = messageValue.trim().length === 0 && !hasSendableAttachment;
  const canSend = !isEmpty && !isSending && !isUploading;
  const isPickerFull = attachments.length >= MAX_ATTACHMENTS;
  // Touch platforms have no modifier to reach for, so Enter has to stay a newline there and the
  // send button is the only way out.
  const sendsOnEnter = usePlatform() === "other";

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!sendsOnEnter || event.key !== "Enter" || event.shiftKey) return;
    // An Enter that confirms an IME composition candidate is not a send.
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canSend) onSendMessage();
  }

  return (
    <TextField
      aria-label={t("components.views.shared.message_composer.message_label")}
      className="flex flex-col rounded-2xl border border-neutral-200 bg-white px-4 pt-3 pb-2 transition-colors focus-within:border-red-400"
      // Frozen while sending so the send's onError draft restore cannot clobber newer typing.
      isDisabled={isSending}
      onChange={(value) => {
        onMessageChange(value);
        onTyping?.();
      }}
      value={messageValue}
    >
      <ComposerAttachments files={attachments} onRemove={onRemoveFile} />
      <AutoGrowTextArea
        className="text-base/6 text-neutral-900 caret-red-400 outline-none placeholder:text-neutral-400"
        enterKeyHint={sendsOnEnter ? "send" : "enter"}
        onKeyDown={handleKeyDown}
        placeholder={t("components.views.shared.message_composer.message_placeholder")}
        value={messageValue}
      />
      <div className="mt-1 flex items-center justify-between">
        <input
          ref={fileInputRef}
          accept={ALLOWED_IMAGE_TYPES.join(",")}
          className="hidden"
          multiple
          onChange={(event) => {
            if (event.target.files?.length) onAddFiles(event.target.files);
            // Clear the input so picking the same file twice still fires a change.
            event.target.value = "";
          }}
          type="file"
        />
        <Button
          aria-label={t("components.views.shared.message_composer.add_image")}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 outline-none transition-colors hover:bg-neutral-50 hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-red-300 disabled:cursor-default disabled:text-neutral-300"
          isDisabled={isPickerFull || isSending}
          onPress={() => fileInputRef.current?.click()}
        >
          <Icon name="image" size="20" />
        </Button>
        <Button
          aria-label={t("components.views.shared.message_composer.send")}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-red-400 pressed:bg-red-600 text-white outline-none transition-colors hover:bg-red-500 focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2 disabled:cursor-default disabled:bg-neutral-100 disabled:text-neutral-400"
          isDisabled={!canSend}
          onPress={onSendMessage}
        >
          <PaperPlaneIcon className="size-4" />
        </Button>
      </div>
    </TextField>
  );
}
